import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { getHandbook } from 'api/clients/HandbookClient'
import { makeAutoObservable, observable, ObservableMap } from 'mobx'
import { defaultMobxCacheMaxAge, type RootStore } from 'stores/RootStore'
import { HandbookItem, HandbookItemType, HandbookV2 } from 'types'
import { HandbookTreeStore } from 'components/Handbook2_0/Stores/HandbookTreeStore'
import { HandbookTreeItem } from 'components/Handbook2_0/Stores/HandbookTreeItem'
import { makePersistable } from 'mobx-persist-store'

type HandbookKey = string
type Handbook = HandbookV2

export class HandbooksStore {
  rootStore: RootStore
  handbooks: ObservableMap<HandbookKey, HandbookTreeStore> = observable.map()
  items: ObservableMap<string, HandbookTreeItem> = observable.map()
  currentHandbook?: HandbookTreeStore
  requests: ObservableMap<HandbookKey, Promise<HandbookTreeStore>> = observable.map()

  constructor(rootStore: RootStore, shouldPersist = true) {
    this.rootStore = rootStore
    makeAutoObservable(this)

    if (shouldPersist) {
      makePersistable(this, {
        name: 'HandbooksStore',
        properties: ['handbooks', 'items'],
        storage: window.localStorage,
        stringify: false,
        expireIn: defaultMobxCacheMaxAge,
      })
    }
  }

  add = (handbook: Handbook) => {
    const store = new HandbookTreeStore(handbook, this)
    this.handbooks.set(handbook.key, store)
    handbook.items.forEach(this.addItem)
    Object.values(store.items).forEach((value) => {
      if (value.data.type === HandbookItemType.NestedHandbook) {
        this.fetch(value.data.nested_handbook.key)
      }
    })
    return store
  }

  bulkAdd = (handbooks: Handbook[]) => {
    handbooks.forEach((handbook) => this.add(handbook))
  }

  get = (key?: HandbookKey) => {
    if (!key) return undefined

    return this.handbooks.get(key)
  }

  setCurrentHandbook = (key?: HandbookKey) => {
    if (key === undefined) {
      this.currentHandbook = undefined
    } else {
      this.currentHandbook = this.get(key)
    }
  }

  fetch = (key: HandbookKey, clear: boolean = false) => {
    return new Promise<HandbookTreeStore>((resolve, reject) => {
      const handbook = this.handbooks.get(key)
      if (handbook && !clear) {
        resolve(handbook)
        return
      }

      let request = this.requests.get(key)
      if (!request) {
        request = getHandbook(key).then((res) => {
          if (res.type !== SUCCESS_RESPONSE_TYPE) {
            if (res.type === 'error' && res.status === 401) throw new Error('unauthorized')
            if (res.type === 'error' && res.status === 403) throw new Error('forbidden')
            throw new Error()
          }
          const handbook = res.data.handbook
          const store = new HandbookTreeStore(handbook, this)
          handbook.items.forEach(this.addItem)
          this.handbooks.set(key, store)
          return store
        })
        this.requests.set(key, request)
      }
      request
        .then(resolve)
        .catch(reject)
        .finally(() => this.requests.delete(key))
    })
  }

  refresh = (key?: HandbookKey) => {
    if (!key) return Promise.resolve()
    const existingHandbook = this.handbooks.get(key)
    if (!existingHandbook) return

    return this.fetch(key, true)
  }

  addItem = (item: HandbookItem) => {
    const isCurrentlyExpanded = this.items.get(item.id.toString())?.isExpanded
    this.items.set(
      item.id.toString(),
      new HandbookTreeItem(this, {
        id: item.id.toString(),
        isExpanded: isCurrentlyExpanded,
        data: item,
      }),
    )
  }

  removeItem = (id: string) => {
    this.items.delete(id)
  }
}
